import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Paper, Popper, TextField, Tooltip } from '@mui/material';
import { LoadingIndicator } from '../loading-indicator.component';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { FormError } from './hook-form-error';
import { QueryKeyConfig } from '../../api/api-helper';

interface Column {
  header: string;
  field: string;
  width?: string;
}

interface AutoCompleteTableSelect {
  name: string;
  dataItems?: any[];
  watchKeys?: string[];
  optionWidth?: string;
  getQueryKeyConfig?: (inputData: any, getValues: any) => QueryKeyConfig;
  onChange: (v: any) => void;
  label: string;
  columns: Column[];
  getOptionLabel: (option: any) => string;
  optionsValueKey: string;
  idKey: string;
  isParentLoading?: boolean;
  disabled?: boolean;
}

const mergeDataItems = (data1: any[], data2: any[], key: string) => {
  const dataArray1 = Array.isArray(data1) ? data1 : [];
  const dataArray2 = Array.isArray(data2) ? data2 : [];
  const result = dataArray1.filter((d) => !dataArray2.some((d2) => d2[key] === d[key]));
  return [...result, ...dataArray2];
};

export const FormAutoCompleteTableSelect = ({
  name,
  dataItems,
  optionWidth,
  watchKeys,
  onChange,
  label,
  columns,
  optionsValueKey,
  getOptionLabel,
  getQueryKeyConfig,
  idKey,
  isParentLoading,
  disabled
}: AutoCompleteTableSelect): any => {

  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [completeData, setCompleteData] = useState<any[]>([]);

  useWatch({
    name: watchKeys || [],
  });

  const query = getQueryKeyConfig?.(
    watchKeys?.reduce((acc: any, key) => ({ ...acc, [key]: getValues(key) }), {}),
    getValues
  );

  const { data, isLoading, isFetching } = useLoadData<ApiResponse<any[]>>(query, !!query);

  useEffect(() => {
    const mergedData = mergeDataItems(data?.data ?? [], dataItems ?? [], optionsValueKey);
    setCompleteData(mergedData);

    const currentValue = getValues(name);

    if (mergedData?.length > 0 && currentValue?.[optionsValueKey]) {
      const newValue = mergedData.find((d) => d[optionsValueKey] === currentValue[optionsValueKey]);
      setValue(name, newValue);
    }
  }, [data, dataItems]);

  const handleChange = (newValue: any, fieldOnChange: any) => {
    fieldOnChange(newValue); // update react-hook-form
    onChange(newValue);      // notify parent
  };

  return (
    <>
      <LoadingIndicator isLoading={isLoading || isFetching || isParentLoading} />
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Autocomplete
            size="small"
            disablePortal
            popupIcon={null}
            options={completeData}
            value={field.value}
            disabled={disabled}
            getOptionLabel={getOptionLabel}
            onChange={(e, newValue) => handleChange(newValue, field.onChange)}

            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "setWidth",
                    enabled: true,
                    phase: "beforeWrite",
                    requires: ["computeStyles"],
                    fn: ({ state }) => {
                      state.styles.popper.width = optionWidth ?? '900px';
                    },
                  },
                ],
              },
            }}


            PaperComponent={(props) => (
              <Paper {...props} style={{ maxHeight: 300, overflow: "auto" }}>
                {/* header */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: columns.map((c) => c.width || "1fr").join(" "),
                    padding: "8px 12px",
                    fontWeight: "bold",
                    borderBottom: "1px solid rgba(0,0,0,0.2)",
                  }}
                >
                  {columns.map((col) => (
                    <span key={col.field}>{col.header}</span>
                  ))}
                </Box>

                {props.children}
              </Paper>
            )}

            renderOption={(props, option) => (
               <li {...props} key={option[idKey]} style={{ padding: 0 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: columns.map((c) => c.width || "1fr").join(" "),
                    width: "100%",
                    padding: "8px 12px",
                  }}
                >
                   {columns.map((col) => {
                    if (col.field === "statusLabel" && option[col.field]) {
                      return (
                        <Tooltip key={col.field} title={
                          option[col.field] === "⚠️ Overridden (Expired)"
                            ? "This Pricing List has been overridden but is now expired. Please update the effective date range; otherwise, default pricing will be applied."
                            : option[col.field] === "⚠️ Overridden"
                              ? "This Pricing List is effective and overridden."
                              : option[col.field] === "✅ Effective"
                              ?" This Pricing List is effective."
                              : ""
                        }>
                          <span>{option[col.field]}</span>
                        </Tooltip>
                      );
                    }
                    return <span key={col.field}>{option[col.field]}</span>;
                  })}
                </Box>
              </li>
            )}

            renderInput={(params) => (
              <TextField {...params} label={label} error={!!errors[name]} />
            )}
          />

        )}
      />

      <FormError name={name} />
    </>
  );
};


FormAutoCompleteTableSelect.defaultProps = {
  optionsValueKey: 'id',
  defaultItemKey: 'name',
  onChange: () => {
    return;
  },
};
